import React from "react";
import { motion } from "framer-motion";
import { FaNewspaper, FaExternalLinkAlt } from "react-icons/fa";

// Import logos
import nywLogo from "../../assets/images/nyw.png";
import wstLogo from "../../assets/images/wst.webp";
import usiLogo from "../../assets/images/usi.jpeg";
import sbLogo from "../../assets/images/sb.png";
import packtLogo from "../../assets/images/packt.png";

const mediaFeatures = [
  {
    id: 1,
    outlet: "New York Weekly",
    title: "Featured for contributions to cybersecurity",
    description:
      "Featured for contributions to cybersecurity and cloud security innovations",
    logo: nywLogo,
    link: "https://nyweekly.com/tech/mihir-shah-a-trailblazer-in-cybersecurity-and-thought-leader-in-the-industry/",
    date: "2023",
    type: "Feature",
  },
  {
    id: 2,
    outlet: "Wall Street Times",
    title: "Leading voice in cloud security",
    description: "Recognized as a leading voice in cloud security solutions",
    logo: wstLogo,
    link: "https://wallstreettimes.com/mihir-shah-pioneering-the-future-of-cybersecurity-through-expertise-and-innovation/",
    date: "2023",
    type: "Feature",
  },
  {
    id: 3,
    outlet: "US Insider",
    title: "Pioneer in cybersecurity",
    description: "Recognized as a pioneer in cybersecurity",
    logo: usiLogo,
    link: "https://usinsider.com/mihir-shah-pioneering-cybersecurity-excellence-and-inspiring-future-innovator/",
    date: "2023",
    type: "Feature",
  },
  {
    id: 4,
    outlet: "Security Boulevard",
    title: "Future trends in cybersecurity",
    description:
      "Interviewed about future trends in cybersecurity and cloud-native security",
    logo: sbLogo,
    link: "https://securityboulevard.com/2024/04/how-to-secure-cloud-native-applications/",
    date: "2024",
    type: "Interview",
  },
  {
    id: 5,
    outlet: "Packt Publishing",
    title: "Cloud security transformations",
    description:
      "Interviewed with Packt about my thoughts on cloud security and transformations in the Industry",
    logo: packtLogo,
    link: "https://partnerships.packt.com/interview-with-mihir-shah/",
    date: "2023",
    type: "Interview",
  },
];

const MediaFeaturesSection = () => {
  return (
    <section className="mb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Media Features & Publications
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {mediaFeatures.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300 group"
            >
              <div className="flex items-start gap-4">
                {/* Logo */}
                <div className="w-16 h-16 bg-white/10 rounded-lg p-2 flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                  {feature.logo ? (
                    <img
                      src={feature.logo}
                      alt={feature.outlet}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <FaNewspaper className="text-2xl text-blue-400" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">
                        {feature.outlet}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                          {feature.type}
                        </span>
                        <span>{feature.date}</span>
                      </div>
                    </div>
                    <a
                      href={feature.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <FaExternalLinkAlt />
                    </a>
                  </div>

                  <h4 className="font-medium text-white mb-1">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-gray-300">{feature.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { label: "Publications", value: "1", icon: "📚" },
            { label: "Media Features", value: "5+", icon: "📰" },
            { label: "Interviews", value: "2", icon: "🎤" },
            { label: "Articles", value: "10+", icon: "📄" },
          ].map((stat, index) => (
            <div
              key={stat.label}
              className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-white/10 rounded-lg p-4 text-center"
            >
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default MediaFeaturesSection;
